<template>
  <div class="access-points-container">
    <!-- Header con acciones -->
    <div class="row mb-4">
      <div class="col-md-8">
        <h3 class="mb-1">
          <i class="nc-icon nc-key-25 text-primary me-2"></i>
          Gestión de Puntos de Acceso (DEMO)
        </h3>
        <p class="text-muted">
          Administra los dispositivos de control de acceso del gimnasio
        </p>
      </div>
      <div class="col-md-4 text-end">
        <button 
          class="btn btn-primary"
          @click="showAddModal = true"
          :disabled="loading.main"
        >
          <i class="fas fa-plus me-2"></i>
          Nuevo Punto de Acceso
        </button>
        <button 
          class="btn btn-outline-secondary ms-2"
          @click="loadAccessPoints"
          :disabled="loading.main"
        >
          <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading.main }"></i>
        </button>
      </div>
    </div>

    <!-- Filtros y búsqueda -->
    <div class="card mb-4">
      <div class="card-body">
        <div class="row">
          <div class="col-md-4">
            <div class="form-group">
              <label class="text-muted small">Buscar por nombre o IP</label>
              <input 
                type="text" 
                class="form-control"
                v-model="filters.search"
                @input="applyFilters"
                placeholder="Ej: Entrada Principal, 192.168.1.100"
              >
            </div>
          </div>
          <div class="col-md-3">
            <div class="form-group">
              <label class="text-muted small">Sucursal</label>
              <select 
                class="form-control"
                v-model="filters.branch"
                @change="applyFilters"
              >
                <option value="">Todas las sucursales</option>
                <option v-for="branch in branches" :key="branch" :value="branch">
                  {{ branch }}
                </option>
              </select>
            </div>
          </div>
          <div class="col-md-3">
            <div class="form-group">
              <label class="text-muted small">Tipo de Dispositivo</label>
              <select 
                class="form-control"
                v-model="filters.type"
                @change="applyFilters"
              >
                <option value="">Todos los tipos</option>
                <option value="torniquete">Torniquete</option>
                <option value="lector">Lector de Tarjetas</option>
                <option value="biometrico">Biométrico</option>
                <option value="camara">Cámara IP</option>
              </select>
            </div>
          </div>
          <div class="col-md-2">
            <div class="form-group">
              <label class="text-muted small">Estado</label>
              <select 
                class="form-control"
                v-model="filters.status"
                @change="applyFilters"
              >
                <option value="">Todos</option>
                <option value="online">En línea</option>
                <option value="offline">Fuera de línea</option>
                <option value="mantenimiento">Mantenimiento</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Estadísticas rápidas -->
    <div class="row mb-4">
      <div class="col-lg-3 col-md-6 mb-3">
        <div class="card bg-primary text-white">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <h5 class="mb-0">{{ stats.total }}</h5>
                <small>Total Dispositivos</small>
              </div>
              <i class="fas fa-server fa-2x opacity-50"></i>
            </div>
          </div>
        </div>
      </div>
      <div class="col-lg-3 col-md-6 mb-3">
        <div class="card bg-success text-white">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <h5 class="mb-0">{{ stats.online }}</h5>
                <small>En línea</small>
              </div>
              <i class="fas fa-check-circle fa-2x opacity-50"></i>
            </div>
          </div>
        </div>
      </div>
      <div class="col-lg-3 col-md-6 mb-3">
        <div class="card bg-danger text-white">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <h5 class="mb-0">{{ stats.offline }}</h5>
                <small>Fuera de línea</small>
              </div>
              <i class="fas fa-times-circle fa-2x opacity-50"></i>
            </div>
          </div>
        </div>
      </div>
      <div class="col-lg-3 col-md-6 mb-3">
        <div class="card bg-warning text-white">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-center">
              <div>
                <h5 class="mb-0">{{ stats.maintenance }}</h5>
                <small>En Mantenimiento</small>
              </div>
              <i class="fas fa-tools fa-2x opacity-50"></i>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Tabla de puntos de acceso -->
    <div class="card">
      <div class="card-body">
        <div v-if="loading.main" class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Cargando...</span>
          </div>
          <p class="mt-3 text-muted">Cargando puntos de acceso...</p>
        </div>

        <div v-else-if="filteredAccessPoints.length === 0" class="text-center py-5">
          <i class="fas fa-door-closed fa-4x text-muted mb-3"></i>
          <h5 class="text-muted">No se encontraron puntos de acceso</h5>
          <p class="text-muted">
            {{ filters.search || filters.branch || filters.type || filters.status ? 
               'Intenta ajustar los filtros de búsqueda' : 
               'Comienza agregando tu primer punto de acceso' }}
          </p>
        </div>

        <div v-else class="table-responsive">
          <table class="table table-hover">
            <thead>
              <tr>
                <th>Estado</th>
                <th>Nombre</th>
                <th>Tipo</th>
                <th>Dirección IP</th>
                <th>Sucursal</th>
                <th>Ubicación</th>
                <th>Último Acceso</th>
                <th class="text-center">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="point in paginatedAccessPoints" :key="point.id">
                <td>
                  <span 
                    class="badge"
                    :class="{
                      'bg-success': point.status === 'online',
                      'bg-danger': point.status === 'offline',
                      'bg-warning': point.status === 'mantenimiento'
                    }"
                  >
                    <i 
                      class="fas me-1"
                      :class="{
                        'fa-check-circle': point.status === 'online',
                        'fa-times-circle': point.status === 'offline',
                        'fa-tools': point.status === 'mantenimiento'
                      }"
                    ></i>
                    {{ getStatusLabel(point.status) }}
                  </span>
                </td>
                <td>
                  <strong>{{ point.name }}</strong>
                  <br>
                  <small class="text-muted">ID: {{ point.id }}</small>
                </td>
                <td>
                  <i 
                    class="fas me-1"
                    :class="getTypeIcon(point.type)"
                  ></i>
                  {{ getTypeLabel(point.type) }}
                </td>
                <td>
                  <code>{{ point.ip }}</code>
                  <br>
                  <small class="text-muted">Puerto: {{ point.port }}</small>
                </td>
                <td>{{ point.branch }}</td>
                <td>{{ point.location }}</td>
                <td>
                  <span v-if="point.lastAccess">
                    {{ formatDate(point.lastAccess) }}
                    <br>
                    <small class="text-muted">{{ formatTime(point.lastAccess) }}</small>
                  </span>
                  <span v-else class="text-muted">-</span>
                </td>
                <td class="text-center">
                  <div class="btn-group btn-group-sm">
                    <button 
                      class="btn btn-outline-info"
                      @click="viewDetails(point)"
                      title="Ver detalles"
                    >
                      <i class="fas fa-eye"></i>
                    </button>
                    <button 
                      class="btn btn-outline-primary"
                      @click="editAccessPoint(point)"
                      title="Editar"
                    >
                      <i class="fas fa-edit"></i>
                    </button>
                    <button 
                      class="btn btn-outline-warning"
                      @click="testConnection(point)"
                      :disabled="loading.test[point.id]"
                      title="Probar conexión"
                    >
                      <i 
                        class="fas fa-network-wired"
                        :class="{ 'fa-spin': loading.test[point.id] }"
                      ></i>
                    </button>
                    <button 
                      class="btn btn-outline-danger"
                      @click="confirmDelete(point)"
                      title="Eliminar"
                    >
                      <i class="fas fa-trash"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Paginación -->
        <div v-if="totalPages > 1" class="d-flex justify-content-between align-items-center mt-4">
          <div class="text-muted">
            Mostrando {{ ((currentPage - 1) * itemsPerPage) + 1 }} - 
            {{ Math.min(currentPage * itemsPerPage, filteredAccessPoints.length) }} 
            de {{ filteredAccessPoints.length }} dispositivos
          </div>
          <nav>
            <ul class="pagination mb-0">
              <li class="page-item" :class="{ disabled: currentPage === 1 }">
                <a class="page-link" href="#" @click.prevent="currentPage = 1">
                  <i class="fas fa-angle-double-left"></i>
                </a>
              </li>
              <li class="page-item" :class="{ disabled: currentPage === 1 }">
                <a class="page-link" href="#" @click.prevent="currentPage--">
                  <i class="fas fa-angle-left"></i>
                </a>
              </li>
              <li 
                v-for="page in displayedPages" 
                :key="page"
                class="page-item" 
                :class="{ active: currentPage === page }"
              >
                <a class="page-link" href="#" @click.prevent="currentPage = page">
                  {{ page }}
                </a>
              </li>
              <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                <a class="page-link" href="#" @click.prevent="currentPage++">
                  <i class="fas fa-angle-right"></i>
                </a>
              </li>
              <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                <a class="page-link" href="#" @click.prevent="currentPage = totalPages">
                  <i class="fas fa-angle-double-right"></i>
                </a>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>

    <!-- Modal para agregar/editar -->
    <div 
      class="modal fade" 
      :class="{ show: showAddModal || showEditModal }"
      :style="{ display: showAddModal || showEditModal ? 'block' : 'none' }"
      tabindex="-1"
    >
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i 
                class="fas me-2"
                :class="showEditModal ? 'fa-edit' : 'fa-plus'"
              ></i>
              {{ showEditModal ? 'Editar' : 'Nuevo' }} Punto de Acceso
            </h5>
            <button 
              type="button" 
              class="btn-close" 
              @click="closeModal"
            ></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="saveAccessPoint">
              <div class="row">
                <div class="col-md-6">
                  <div class="form-group mb-3">
                    <label class="form-label">Nombre del Dispositivo <span class="text-danger">*</span></label>
                    <input 
                      type="text" 
                      class="form-control"
                      v-model="formData.name"
                      required
                      placeholder="Ej: Entrada Principal"
                    >
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group mb-3">
                    <label class="form-label">Tipo de Dispositivo <span class="text-danger">*</span></label>
                    <select 
                      class="form-control"
                      v-model="formData.type"
                      required
                    >
                      <option value="">Seleccione un tipo</option>
                      <option value="torniquete">Torniquete</option>
                      <option value="lector">Lector de Tarjetas</option>
                      <option value="biometrico">Biométrico</option>
                      <option value="camara">Cámara IP</option>
                    </select>
                  </div>
                </div>
              </div>

              <div class="row">
                <div class="col-md-6">
                  <div class="form-group mb-3">
                    <label class="form-label">Dirección IP <span class="text-danger">*</span></label>
                    <input 
                      type="text" 
                      class="form-control"
                      v-model="formData.ip"
                      required
                      pattern="^(?:[0-9]{1,3}\.){3}[0-9]{1,3}$"
                      placeholder="192.168.1.100"
                    >
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group mb-3">
                    <label class="form-label">Puerto</label>
                    <input 
                      type="number" 
                      class="form-control"
                      v-model="formData.port"
                      min="1"
                      max="65535"
                      placeholder="8080"
                    >
                  </div>
                </div>
              </div>

              <div class="row">
                <div class="col-md-6">
                  <div class="form-group mb-3">
                    <label class="form-label">Sucursal <span class="text-danger">*</span></label>
                    <select 
                      class="form-control"
                      v-model="formData.branch"
                      required
                    >
                      <option value="">Seleccione una sucursal</option>
                      <option v-for="branch in branches" :key="branch" :value="branch">
                        {{ branch }}
                      </option>
                    </select>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group mb-3">
                    <label class="form-label">Ubicación Física</label>
                    <input 
                      type="text" 
                      class="form-control"
                      v-model="formData.location"
                      placeholder="Ej: Piso 1, Entrada Norte"
                    >
                  </div>
                </div>
              </div>

              <div class="row">
                <div class="col-md-12">
                  <div class="form-group mb-3">
                    <label class="form-label">Descripción</label>
                    <textarea 
                      class="form-control"
                      v-model="formData.description"
                      rows="3"
                      placeholder="Información adicional sobre el dispositivo..."
                    ></textarea>
                  </div>
                </div>
              </div>

              <div class="row">
                <div class="col-md-6">
                  <div class="form-check form-switch mb-3">
                    <input 
                      class="form-check-input" 
                      type="checkbox" 
                      id="activeSwitch"
                      v-model="formData.active"
                    >
                    <label class="form-check-label" for="activeSwitch">
                      Dispositivo Activo
                    </label>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-check form-switch mb-3">
                    <input 
                      class="form-check-input" 
                      type="checkbox" 
                      id="autoReconnectSwitch"
                      v-model="formData.autoReconnect"
                    >
                    <label class="form-check-label" for="autoReconnectSwitch">
                      Reconexión Automática
                    </label>
                  </div>
                </div>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button 
              type="button" 
              class="btn btn-secondary" 
              @click="closeModal"
            >
              Cancelar
            </button>
            <button 
              type="button" 
              class="btn btn-primary"
              @click="saveAccessPoint"
              :disabled="loading.save"
            >
              <span v-if="loading.save">
                <i class="fas fa-spinner fa-spin me-2"></i>
                Guardando...
              </span>
              <span v-else>
                <i class="fas fa-save me-2"></i>
                Guardar
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal backdrop -->
    <div 
      v-if="showAddModal || showEditModal"
      class="modal-backdrop fade show"
      @click="closeModal"
    ></div>
  </div>
</template>

<script>
export default {
  name: 'AccessPoints',
  data() {
    return {
      accessPoints: [],
      filteredAccessPoints: [],
      branches: ['Sucursal Central', 'Sucursal Norte', 'Sucursal Sur'],
      loading: {
        main: false,
        save: false,
        test: {}
      },
      filters: {
        search: '',
        branch: '',
        type: '',
        status: ''
      },
      currentPage: 1,
      itemsPerPage: 10,
      showAddModal: false,
      showEditModal: false,
      formData: {
        name: '',
        type: '',
        ip: '',
        port: 8080,
        branch: '',
        location: '',
        description: '',
        active: true,
        autoReconnect: true
      },
      editingId: null,
      stats: {
        total: 0,
        online: 0,
        offline: 0,
        maintenance: 0
      }
    }
  },
  computed: {
    totalPages() {
      return Math.ceil(this.filteredAccessPoints.length / this.itemsPerPage)
    },
    paginatedAccessPoints() {
      const start = (this.currentPage - 1) * this.itemsPerPage
      const end = start + this.itemsPerPage
      return this.filteredAccessPoints.slice(start, end)
    },
    displayedPages() {
      const pages = []
      const maxPages = 5
      let start = Math.max(1, this.currentPage - Math.floor(maxPages / 2))
      let end = Math.min(this.totalPages, start + maxPages - 1)
      
      if (end - start + 1 < maxPages) {
        start = Math.max(1, end - maxPages + 1)
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i)
      }
      
      return pages
    }
  },
  mounted() {
    this.loadAccessPoints()
  },
  methods: {
    async loadAccessPoints() {
      try {
        this.loading.main = true
        
        // Simular carga de datos (en producción esto vendría de la API)
        await new Promise(resolve => setTimeout(resolve, 1500))
        
        // Datos de ejemplo
        this.accessPoints = [
          {
            id: 'AP001',
            name: 'Entrada Principal',
            type: 'torniquete',
            ip: '192.168.1.100',
            port: 8080,
            branch: 'Sucursal Central',
            location: 'Piso 1, Entrada Norte',
            status: 'online',
            lastAccess: new Date(Date.now() - 1000 * 60 * 5),
            description: 'Torniquete bidireccional con lector de tarjetas integrado',
            active: true,
            autoReconnect: true
          },
          {
            id: 'AP002',
            name: 'Entrada Parking',
            type: 'lector',
            ip: '192.168.1.101',
            port: 8080,
            branch: 'Sucursal Central',
            location: 'Sótano 1, Entrada vehicular',
            status: 'online',
            lastAccess: new Date(Date.now() - 1000 * 60 * 15),
            description: 'Lector de tarjetas RFID para acceso vehicular',
            active: true,
            autoReconnect: true
          },
          {
            id: 'AP003',
            name: 'Área VIP',
            type: 'biometrico',
            ip: '192.168.1.102',
            port: 8080,
            branch: 'Sucursal Norte',
            location: 'Piso 2, Zona VIP',
            status: 'offline',
            lastAccess: new Date(Date.now() - 1000 * 60 * 60 * 2),
            description: 'Lector biométrico de huella digital',
            active: true,
            autoReconnect: false
          },
          {
            id: 'AP004',
            name: 'Cámara Entrada',
            type: 'camara',
            ip: '192.168.1.150',
            port: 554,
            branch: 'Sucursal Sur',
            location: 'Entrada principal',
            status: 'mantenimiento',
            lastAccess: null,
            description: 'Cámara IP con reconocimiento facial',
            active: false,
            autoReconnect: true
          }
        ]
        
        this.applyFilters()
        this.updateStats()
        
      } catch (error) {
        this.$logger.error('Error al cargar puntos de acceso:', error)
        this.$swal.fire({
          icon: 'error',
          title: 'Error al cargar datos',
          text: 'No se pudieron cargar los puntos de acceso. Por favor, intenta nuevamente.',
          confirmButtonColor: '#6bd098'
        })
      } finally {
        this.loading.main = false
      }
    },
    
    applyFilters() {
      let filtered = [...this.accessPoints]
      
      // Filtro de búsqueda
      if (this.filters.search) {
        const search = this.filters.search.toLowerCase()
        filtered = filtered.filter(point => 
          point.name.toLowerCase().includes(search) ||
          point.ip.includes(search) ||
          point.id.toLowerCase().includes(search)
        )
      }
      
      // Filtro de sucursal
      if (this.filters.branch) {
        filtered = filtered.filter(point => point.branch === this.filters.branch)
      }
      
      // Filtro de tipo
      if (this.filters.type) {
        filtered = filtered.filter(point => point.type === this.filters.type)
      }
      
      // Filtro de estado
      if (this.filters.status) {
        filtered = filtered.filter(point => point.status === this.filters.status)
      }
      
      this.filteredAccessPoints = filtered
      this.currentPage = 1
    },
    
    updateStats() {
      this.stats.total = this.accessPoints.length
      this.stats.online = this.accessPoints.filter(p => p.status === 'online').length
      this.stats.offline = this.accessPoints.filter(p => p.status === 'offline').length
      this.stats.maintenance = this.accessPoints.filter(p => p.status === 'mantenimiento').length
    },
    
    getStatusLabel(status) {
      const labels = {
        online: 'En línea',
        offline: 'Fuera de línea',
        mantenimiento: 'Mantenimiento'
      }
      return labels[status] || status
    },
    
    getTypeLabel(type) {
      const labels = {
        torniquete: 'Torniquete',
        lector: 'Lector de Tarjetas',
        biometrico: 'Biométrico',
        camara: 'Cámara IP'
      }
      return labels[type] || type
    },
    
    getTypeIcon(type) {
      const icons = {
        torniquete: 'fa-door-open',
        lector: 'fa-id-card',
        biometrico: 'fa-fingerprint',
        camara: 'fa-video'
      }
      return icons[type] || 'fa-server'
    },
    
    formatDate(date) {
      if (!date) return '-'
      const d = new Date(date)
      return d.toLocaleDateString('es-CL')
    },
    
    formatTime(date) {
      if (!date) return '-'
      const d = new Date(date)
      return d.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })
    },
    
    viewDetails(point) {
      this.$swal.fire({
        title: point.name,
        html: `
          <div class="text-start">
            <p><strong>ID:</strong> ${point.id}</p>
            <p><strong>Tipo:</strong> ${this.getTypeLabel(point.type)}</p>
            <p><strong>IP:</strong> ${point.ip}:${point.port}</p>
            <p><strong>Sucursal:</strong> ${point.branch}</p>
            <p><strong>Ubicación:</strong> ${point.location}</p>
            <p><strong>Estado:</strong> <span class="badge bg-${point.status === 'online' ? 'success' : point.status === 'offline' ? 'danger' : 'warning'}">${this.getStatusLabel(point.status)}</span></p>
            <p><strong>Último acceso:</strong> ${point.lastAccess ? this.formatDate(point.lastAccess) + ' ' + this.formatTime(point.lastAccess) : 'Nunca'}</p>
            <p><strong>Descripción:</strong> ${point.description || 'Sin descripción'}</p>
            <p><strong>Activo:</strong> ${point.active ? 'Sí' : 'No'}</p>
            <p><strong>Reconexión automática:</strong> ${point.autoReconnect ? 'Sí' : 'No'}</p>
          </div>
        `,
        icon: 'info',
        confirmButtonColor: '#6bd098'
      })
    },
    
    editAccessPoint(point) {
      this.editingId = point.id
      this.formData = { ...point }
      this.showEditModal = true
    },
    
    async testConnection(point) {
      try {
        this.$set(this.loading.test, point.id, true)
        
        // Simular prueba de conexión
        await new Promise(resolve => setTimeout(resolve, 2000))
        
        // Simular resultado aleatorio para demostración
        const success = Math.random() > 0.3
        
        if (success) {
          this.$swal.fire({
            icon: 'success',
            title: 'Conexión exitosa',
            text: `El dispositivo ${point.name} respondió correctamente.`,
            confirmButtonColor: '#6bd098'
          })
          
          // Actualizar estado si estaba offline
          if (point.status === 'offline') {
            point.status = 'online'
            this.updateStats()
          }
        } else {
          this.$swal.fire({
            icon: 'error',
            title: 'Error de conexión',
            text: `No se pudo establecer conexión con ${point.name}.`,
            confirmButtonColor: '#6bd098'
          })
        }
        
      } catch (error) {
        this.$logger.error('Error al probar conexión:', error)
        this.$swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Ocurrió un error al probar la conexión.',
          confirmButtonColor: '#6bd098'
        })
      } finally {
        this.$set(this.loading.test, point.id, false)
      }
    },
    
    confirmDelete(point) {
      this.$swal.fire({
        title: '¿Eliminar punto de acceso?',
        html: `¿Estás seguro de que deseas eliminar <strong>${point.name}</strong>?<br>Esta acción no se puede deshacer.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#dc3545',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Sí, eliminar',
        cancelButtonText: 'Cancelar'
      }).then((result) => {
        if (result.isConfirmed) {
          this.deleteAccessPoint(point)
        }
      })
    },
    
    async deleteAccessPoint(point) {
      try {
        // Simular eliminación
        await new Promise(resolve => setTimeout(resolve, 1000))
        
        // Eliminar de la lista
        const index = this.accessPoints.findIndex(p => p.id === point.id)
        if (index > -1) {
          this.accessPoints.splice(index, 1)
          this.applyFilters()
          this.updateStats()
        }
        
        this.$swal.fire({
          icon: 'success',
          title: 'Eliminado',
          text: 'El punto de acceso ha sido eliminado correctamente.',
          confirmButtonColor: '#6bd098'
        })
        
      } catch (error) {
        this.$logger.error('Error al eliminar:', error)
        this.$swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudo eliminar el punto de acceso.',
          confirmButtonColor: '#6bd098'
        })
      }
    },
    
    async saveAccessPoint() {
      try {
        // Validación básica
        if (!this.formData.name || !this.formData.type || !this.formData.ip || !this.formData.branch) {
          this.$swal.fire({
            icon: 'warning',
            title: 'Campos requeridos',
            text: 'Por favor, completa todos los campos obligatorios.',
            confirmButtonColor: '#6bd098'
          })
          return
        }
        
        this.loading.save = true
        
        // Simular guardado
        await new Promise(resolve => setTimeout(resolve, 1500))
        
        if (this.showEditModal) {
          // Actualizar existente
          const index = this.accessPoints.findIndex(p => p.id === this.editingId)
          if (index > -1) {
            this.accessPoints[index] = {
              ...this.formData,
              id: this.editingId,
              status: this.formData.active ? 'online' : 'offline'
            }
          }
        } else {
          // Crear nuevo
          const newPoint = {
            ...this.formData,
            id: 'AP' + String(this.accessPoints.length + 1).padStart(3, '0'),
            status: this.formData.active ? 'online' : 'offline',
            lastAccess: null
          }
          this.accessPoints.push(newPoint)
        }
        
        this.applyFilters()
        this.updateStats()
        this.closeModal()
        
        this.$swal.fire({
          icon: 'success',
          title: 'Guardado exitoso',
          text: `El punto de acceso ha sido ${this.showEditModal ? 'actualizado' : 'creado'} correctamente.`,
          confirmButtonColor: '#6bd098'
        })
        
      } catch (error) {
        this.$logger.error('Error al guardar:', error)
        this.$swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudo guardar el punto de acceso.',
          confirmButtonColor: '#6bd098'
        })
      } finally {
        this.loading.save = false
      }
    },
    
    closeModal() {
      this.showAddModal = false
      this.showEditModal = false
      this.editingId = null
      this.resetForm()
    },
    
    resetForm() {
      this.formData = {
        name: '',
        type: '',
        ip: '',
        port: 8080,
        branch: '',
        location: '',
        description: '',
        active: true,
        autoReconnect: true
      }
    }
  }
}
</script>

<style scoped>
.access-points-container {
  padding: 1rem;
}

.modal.show {
  display: block;
  background-color: rgba(0, 0, 0, 0.5);
}

.table td {
  vertical-align: middle;
}

code {
  background-color: #f8f9fa;
  padding: 0.2rem 0.4rem;
  border-radius: 0.25rem;
  font-size: 0.875rem;
}

.opacity-50 {
  opacity: 0.5;
}
</style>
